import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContainer, Flex, ProgressCircle, ToastQueue, View } from '@adobe/react-spectrum';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { ListDocumentCategoryRequest } from '../../../../services/soap/document_library/requests/ListDocumentCategoryRequest';
import DocumentUpload from '../../../../components/protected/layout-components/DocumentComponent/DocumentUpload';
import { UpdateTaskRequest } from '../../../../services/soap/scheduler/requests/UpdateTaskRequest';

interface ITaskDocumentUploadDialogProps {
  isOpen: boolean;
  task: any;
  onClose: () => void;
  onUploaded: () => Promise<void> | void;
}

const buildCategoryTree = (data: any[]) => {
  const map: Record<string, any> = {};
  const roots: any[] = [];

  data.forEach((item: any) => {
    map[item.id] = { ...item, subChild: [] };
  });

  data.forEach((item: any) => {
    if (item.parent && map[item.parent]) {
      map[item.parent].subChild.push(map[item.id]);
    } else if (map[item.id]) {
      roots.push(map[item.id]);
    }
  });

  return roots;
};

const getFirstCategoryId = (items: any[]): string => {
  if (!items.length) return '';
  const first = items[0];
  if (!first) return '';
  if (first.id) return String(first.id);
  if (Array.isArray(first.subChild) && first.subChild.length > 0) {
    return getFirstCategoryId(first.subChild);
  }
  return '';
};

const normalizeTaskDocuments = (documentValue: any): Array<{ documentId: string; name: string }> => {
  if (!documentValue) return [];
  const documents = Array.isArray(documentValue) ? documentValue : [documentValue];
  return documents
    .map((item: any) => ({
      documentId: String(item?.documentId || item?.id || ''),
      name: String(item?.name || item?.fileName || ''),
    }))
    .filter((item) => item.documentId);
};

const TaskDocumentUploadDialog: React.FC<ITaskDocumentUploadDialogProps> = ({
  isOpen,
  task,
  onClose,
  onUploaded,
}) => {
  const { documentLibraryService, schedulerService, store } = useDependency();
  const [isLoading, setIsLoading] = useState(false);
  const [categoryTree, setCategoryTree] = useState<any[]>([]);

  useEffect(() => {
    let isActive = true;

    const loadCategories = async () => {
      if (!isOpen) return;
      setIsLoading(true);
      try {
        const response: any = await documentLibraryService.listDocumentCategory(
          new ListDocumentCategoryRequest(store.Server, store.SessionId)
        );
        const categories = Array.isArray(response?.item)
          ? response.item
          : Array.isArray(response)
            ? response
            : [];

        if (isActive) {
          setCategoryTree(buildCategoryTree(categories));
        }
      } catch {
        if (isActive) {
          setCategoryTree([]);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadCategories();
    return () => {
      isActive = false;
    };
  }, [documentLibraryService, isOpen, store.Server, store.SessionId]);

  const defaultCategoryId = useMemo(() => getFirstCategoryId(categoryTree), [categoryTree]);

  const taskTreeHandle = useMemo(() => {
    const activityId = String(task?.activityId || '').trim();
    if (activityId && activityId !== '0') return activityId;

    const projectId = String(task?.projectId || '').trim();
    if (projectId && projectId !== '0') return projectId;

    const folderId = String(task?.folderId || '').trim();
    if (folderId && folderId !== '0') return folderId;

    const userContactTreeHandle = String(store?.session?.organization?.USERCONTACT?.treeHandle || '').trim();
    if (userContactTreeHandle && userContactTreeHandle !== '0') return userContactTreeHandle;

    return '';
  }, [task?.activityId, task?.folderId, task?.projectId, store?.session?.organization?.USERCONTACT?.treeHandle]);

  const selectedItem = useMemo(
    () => ({
      id: taskTreeHandle,
    }),
    [taskTreeHandle]
  );

  const handleUploadedDocuments = async (documents: Array<{ documentId: string; name: string }>) => {
    const existingDocuments = normalizeTaskDocuments(task?.DOCUMENT);
    const mergedDocuments = [...existingDocuments];

    documents.forEach((document) => {
      const existingIndex = mergedDocuments.findIndex((item) => String(item.documentId) === String(document.documentId));
      if (existingIndex >= 0) {
        mergedDocuments[existingIndex] = {
          documentId: String(document.documentId),
          name: document.name,
        };
      } else {
        mergedDocuments.push({
          documentId: String(document.documentId),
          name: document.name,
        });
      }
    });

    const payload = {
      ...task,
      ident: String(task?.ident || task?.id || ''),
      headId: String(task?.headId || task?.ident || task?.id || ''),
      DOCUMENT: mergedDocuments,
    };

    const response: any = await schedulerService.updateTask(
      new UpdateTaskRequest(store.Server, store.SessionId, payload)
    );

    if (response?.result !== 'OK') {
      throw new Error(response?.EXCEPTION?.message || response?.EXCEPTION?.reason || 'Unable to link uploaded document.');
    }

    await onUploaded();
    ToastQueue.positive('Document uploaded successfully.', { timeout: 2000 });
  };

  return (
    <DialogContainer onDismiss={onClose}>
      {isOpen ? (
        isLoading ? (
          <Dialog>
            <Flex width="100%" justifyContent="center" marginTop={10}>
              <ProgressCircle aria-label="Loading document categories" isIndeterminate />
            </Flex>
          </Dialog>
        ) : !taskTreeHandle ? (
          <Dialog>
            <View padding="size-200">
              Upload is unavailable because no valid upload node is available for this task.
            </View>
          </Dialog>
        ) : (
          <View>
            <DocumentUpload
              categoryId={defaultCategoryId}
              documentCategoryData={categoryTree}
              getDocumentsData={onUploaded}
              close={onClose}
              selectedItem={selectedItem as any}
              contextIdOverride={String(task?.ident || task?.id || '')}
              onUploadedDocuments={handleUploadedDocuments}
            />
          </View>
        )
      ) : null}
    </DialogContainer>
  );
};

export default TaskDocumentUploadDialog;
